/**
 * Cálculo puro del descuento por línea de carrito, dado el conjunto de promociones
 * activas (leídas de la DB en el servidor — nunca del cliente). `findItemConflicts`
 * (apps/api/src/services/promotions.ts) ya impide que dos promociones activas cubran
 * el mismo catalogId a la vez, así que el caso normal es 0 o 1 coincidencia; el caso de
 * >1 solo puede darse por una condición de carrera al crear promociones en el admin.
 */

export interface PromotionForDiscount {
	id: string
	discountPercent: number
	itemIds: string[]
}

export interface DiscountLineInput {
	/** catalogId resuelto del priceId de la línea, o null si no está en el catálogo. */
	catalogId: string | null
	/** Precio de lista por unidad, en centavos (ya verificado contra Stripe). */
	unitAmount: number
	quantity: number
}

export interface DiscountLineResult {
	catalogId: string | null
	unitAmount: number
	/** Precio unitario ya con el descuento aplicado. */
	discountedUnitAmount: number
	quantity: number
	promotionId: string | null
	discountPercent: number
	/** (unitAmount - discountedUnitAmount) * quantity. */
	lineDiscountTotal: number
	/** Presente solo si >1 promoción activa coincide con este catalogId (condición de carrera admin). */
	conflictPromotionIds?: string[]
}

function pickPromotion(catalogId: string | null, promotions: readonly PromotionForDiscount[]): PromotionForDiscount[] {
	if (!catalogId) return []
	return promotions.filter(p => p.itemIds.includes(catalogId))
}

/**
 * Aplica un % de descuento a un monto en centavos, con el MISMO redondeo que usa Stripe/
 * PayPal al cobrar (ver computeLineDiscounts). El carrito (apps/web/src/content/prices.ts)
 * reutiliza esta misma función para que el precio que se le muestra al cliente sea
 * exactamente el que se le cobra — nunca reimplementes este redondeo en otro lado.
 */
export function applyPercentDiscount(amountCents: number, discountPercent: number): number {
	return amountCents - Math.round((amountCents * discountPercent) / 100)
}

export function computeLineDiscounts(
	lines: readonly DiscountLineInput[],
	activePromotions: readonly PromotionForDiscount[]
): DiscountLineResult[] {
	return lines.map(line => {
		const matches = pickPromotion(line.catalogId, activePromotions)

		if (matches.length === 0) {
			return {
				catalogId: line.catalogId,
				unitAmount: line.unitAmount,
				discountedUnitAmount: line.unitAmount,
				quantity: line.quantity,
				promotionId: null,
				discountPercent: 0,
				lineDiscountTotal: 0
			}
		}

		// Empate en cobertura no puede pasar (findItemConflicts ya lo impide en operación
		// normal); si dos promos activas igual coinciden, gana el descuento mayor — nunca
		// se bloquea un pago real por una colisión de datos entre dos promociones admin.
		const winner = matches.reduce((best, p) => (p.discountPercent > best.discountPercent ? p : best), matches[0]!)
		const discountedUnitAmount = applyPercentDiscount(line.unitAmount, winner.discountPercent)
		const conflictPromotionIds = matches.length > 1 ? matches.map(p => p.id) : undefined

		return {
			catalogId: line.catalogId,
			unitAmount: line.unitAmount,
			discountedUnitAmount,
			quantity: line.quantity,
			promotionId: winner.id,
			discountPercent: winner.discountPercent,
			lineDiscountTotal: (line.unitAmount - discountedUnitAmount) * line.quantity,
			...(conflictPromotionIds ? { conflictPromotionIds } : {})
		}
	})
}
